import * as React from 'react';

import { useCurrentUser } from '@docavenue/components';

import { LOCALSTORAGE_KEYS } from '@/src/constants';
import { isCompleted } from '@/components/molecules/WithAuth/functions';
import { PatientUser } from '@/components/molecules/WithAuth/types';
import useCreateInsuranceContract from '@/src/hooks/insurances/useCreateInsuranceContract';
import { BaseAMCUserDataType } from '@/src/types/AMCUserData';

const AMCDetector = () => {
  const user = useCurrentUser() as PatientUser | null;
  const [amcData, setAmcData] = React.useState<BaseAMCUserDataType>();
  const submitted = React.useRef<boolean>(false);
  const { mutate } = useCreateInsuranceContract({
    onMutate: () => {
      return {
        amcNumber: amcData?.amcNumber,
        conventionType: amcData?.conventionType,
        csr: amcData?.csr,
      };
    },
    onSuccess: () => {
      localStorage.removeItem(LOCALSTORAGE_KEYS.AMC_USER_DATA);
    },
  });

  React.useEffect(() => {
    const amcUserData = localStorage.getItem(LOCALSTORAGE_KEYS.AMC_USER_DATA);

    if (
      // No data in local storage
      !amcUserData ||
      // User is not connected
      !user ||
      // User has not confirmed SMS or has not filled profile
      isCompleted(user, true) !== true
    ) {
      return;
    }

    try {
      const parsedAMCUserData = JSON.parse(amcUserData);

      // By default, any AMC added by link will be assigned to the main user profile
      // also, if the link doesn't contain "firstName" and/or "lastName", it will
      // fallback to the main user's "firstName" and "lastName"
      ['firstName', 'lastName', 'patientProfileId'].forEach(key => {
        if (!parsedAMCUserData[key]) {
          parsedAMCUserData[key] =
            user.userPatientInformation.userPatientProfiles?.[0][
              key === 'patientProfileId' ? 'id' : key
            ];
        }
      });

      setAmcData(parsedAMCUserData);
    } catch (e) {
      console.error('Error while parsing AMC user data');
    }
  }, [user]);

  React.useEffect(() => {
    if (!submitted.current && amcData) {
      submitted.current = true;
      mutate({ $payload: { ...amcData, amcId: amcData.amcId || '' } });
    }
  }, [amcData]);

  return null;
};

export default AMCDetector;
